import { useEffect, useRef, useState } from "react";
import { createComponentWithProxy } from "react-fela";

import { expand } from "~/styleguide/utils";
import AspectContainer from "~/components/shared/aspect-container";

export default function DeferredEmbed({
    aspect = 9 / 16,
    className,
    html,
    immediate = false,
    rootMargin = "600px"
}) {
    const ref = useRef(null);
    const [shouldRender, setShouldRender] = useState(immediate);

    useEffect(() => {
        if (shouldRender || !html || typeof window === "undefined") return;

        const node = ref.current;
        if (!node) return;

        if (!("IntersectionObserver" in window)) {
            setShouldRender(true);
            return;
        }

        const observer = new IntersectionObserver(
            entries => {
                if (entries.some(entry => entry.isIntersecting)) {
                    setShouldRender(true);
                    observer.disconnect();
                }
            },
            { rootMargin }
        );

        observer.observe(node);

        return () => observer.disconnect();
    }, [html, rootMargin, shouldRender]);

    if (shouldRender) {
        return <AspectContainer aspect={aspect} className={className} html={html} />;
    }

    return (
        <div ref={ref} style={{ position: "relative" }}>
            <Aspect aspect={aspect} className={className || "aspect-container"} />
        </div>
    );
}

const Aspect = createComponentWithProxy(
    ({ aspect }) => ({
        width: "100%",
        height: 0,
        paddingBottom: `${aspect * 100}%`,
        "> iframe": {
            ...expand()
        }
    }),
    "div"
);
